import { api } from "../config/api";
import { post } from "../api";

export interface UsersSessionArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export type UserVerificationStatus = "invited" | "invite_expired" | "active" | "deactivated";

export interface UserApiItem {
  user_id?: string | number;
  id?: string | number;
  userId?: string | number;
  name?: string;
  first_name?: string;
  middle_name?: string;
  surname?: string;
  username?: string;
  email?: string;
  role?: string;
  role_name?: string;
  business_role?: string;
  business_role_shortcode?: string;
  role_shortcode?: string;
  is_active?: boolean;
  employee_loggedin?: boolean;
  last_active?: string;
  email_verified?: boolean;
  verification_status?: UserVerificationStatus | string;
  invite_expires_on?: string | null;
  active_sessions?: Array<{
    logged_in_time?: string;
    login_time?: string;
    refresh_time?: string;
    last_active?: string;
    [key: string]: unknown;
  }>;
  client_pan?: string;
}

export interface BusinessRoleApiItem {
  role_id?: number;
  business_id?: number;
  shortcode?: string;
  name?: string;
}

export interface GetBusinessTeamResponse {
  success?: boolean;
  data?: { business_team?: UserApiItem[] };
  errors?: string;
}

export interface GetBusinessDetailResponse {
  success?: boolean;
  data?: { business_detail?: { shortcode?: string } };
  errors?: string;
}

export interface GetBusinessRolesResponse {
  success?: boolean;
  data?: { roles?: BusinessRoleApiItem[] };
  errors?: string;
}

export interface CreateUserPayload {
  user_id?: string;
  first_name: string;
  middle_name?: string;
  surname: string;
  username: string;
  email: string;
  is_active: boolean;
  role: string;
  password: string;
  business_shortcode: string;
  business_role: string;
  device_token: string;
  session_id: string;
  client_type?: string;
  org_name?: string;
}

export interface CreateUserResponse {
  success?: boolean;
  message?: string;
  errors?: string | string[];
  data?: {
    user?: {
      username?: string;
      user_id?: number | string;
      email?: string;
      role?: string;
      business?: string;
      set_password_email_sent?: boolean;
    };
  };
}

export interface CheckAadharPayload {
  business_id: number;
  aadhar_number: string;
  device_token: string;
  session_id: string;
}

export interface CheckAadharMatchData {
  client_id: number | string;
  client_name?: string;
  client_aadhar_number?: string;
  client_email?: string;
}
export interface CheckAadharResponse {
  success: boolean;
  message?: string;
  data?: CheckAadharMatchData | null;
  errors?: string[] | string;
  meta?: Record<string, unknown>;
  error_code?: string | number | null;
}

function getBusinessTeam(session: UsersSessionArgs): Promise<GetBusinessTeamResponse> {
  return post<GetBusinessTeamResponse>(api.GET_BUSINESS_TEAM, session);
}

function getBusinessDetail(session: UsersSessionArgs): Promise<GetBusinessDetailResponse> {
  return post<GetBusinessDetailResponse>(api.GET_BUSINESS_DETAIL, session);
}

function getBusinessRoles(session: UsersSessionArgs): Promise<GetBusinessRolesResponse> {
  return post<GetBusinessRolesResponse>(api.GET_BUSINESS_ROLES, session);
}

function createUser(payload: CreateUserPayload): Promise<CreateUserResponse> {
  const body = { ...payload };
  if (body.user_id === "" || body.user_id === undefined) {
    delete body.user_id;
  }
  return post<CreateUserResponse>(api.CREATE_USER, body);
}

export interface SendUserInvitePayload {
  business_id: string | number;
  user_id?: string | number;
  email?: string;
  device_token: string;
  session_id: string;
}

export interface SendUserInviteResponse {
  success?: boolean;
  message?: string;
  errors?: string | string[];
  data?: {
    user?: {
      user_id?: number | string;
      username?: string;
      email?: string;
      invite_email_sent?: boolean;
      verification_status?: string;
      email_verified?: boolean;
      invite_expires_on?: string | null;
    };
  };
}

export interface DeleteInvitedUserPayload {
  business_id: string | number;
  user_id: string | number;
  device_token: string;
  session_id: string;
}

function sendInvite(payload: SendUserInvitePayload): Promise<SendUserInviteResponse> {
  return post<SendUserInviteResponse>(api.SEND_USER_INVITE, payload);
}

function deleteInvitedUser(
  payload: DeleteInvitedUserPayload
): Promise<{ success?: boolean; errors?: string | string[]; message?: string }> {
  return post(api.DELETE_INVITED_USER, payload);
}

function checkAadhar(payload: CheckAadharPayload): Promise<CheckAadharResponse> {
  return post<CheckAadharResponse>(api.CHECK_AADHAR, payload);
}

export interface BusinessSecurityData {
  security_id?: number | null;
  business_id?: number;
  business_name?: string;
  enable_otp?: boolean;
  enabled_by?: string | null;
  enabled_at?: string | null;
}

export interface GetBusinessSecurityResponse {
  success?: boolean;
  message?: string;
  data?: BusinessSecurityData | null;
  errors?: string | string[] | null;
}

export interface AddBusinessSecurityPayload {
  business_id: string | number;
  device_token: string;
  session_id: string;
  enable_otp: boolean;
  security_id?: number | null;
}

export interface AddBusinessSecurityResponse {
  success?: boolean;
  message?: string;
  data?: BusinessSecurityData | null;
  errors?: string | string[] | null;
}

function getBusinessSecurity(session: UsersSessionArgs): Promise<GetBusinessSecurityResponse> {
  return post<GetBusinessSecurityResponse>(api.GET_BUSINESS_SECURITY, session);
}

function addBusinessSecurity(
  payload: AddBusinessSecurityPayload
): Promise<AddBusinessSecurityResponse> {
  const body: Record<string, unknown> = {
    business_id: payload.business_id,
    device_token: payload.device_token,
    session_id: payload.session_id,
    enable_otp: payload.enable_otp,
  };
  if (payload.security_id !== null && payload.security_id !== undefined) {
    body.security_id = payload.security_id;
  }
  return post<AddBusinessSecurityResponse>(api.ADD_BUSINESS_SECURITY, body);
}

export const usersService = {
  getBusinessTeam,
  getBusinessDetail,
  getBusinessRoles,
  createUser,
  sendInvite,
  deleteInvitedUser,
  checkAadhar,
  getBusinessSecurity,
  addBusinessSecurity,
};
